import { useEffect, useState, useCallback } from "react";
import type { ReactNode } from "react";
import { Card, CardContent, Fade, Link, Popper, Stack, Typography } from "@mui/material";
import type { LinkProps } from "@mui/material";
import { routesConfig } from "config/app-config";
import { communesService } from "services";
import TypographySkeleton from "./TypographySkeleton";
import { TypeCommuneAssocieeDeleguee } from "models";
import type { CommuneAssocieeDeleguee, Commune } from "models";
import { useErrorHandler } from "utils/errorHandling";
import { useSearch } from "providers";
import SmsIcon from "@mui/icons-material/Sms";
import { popperModifiers, usePopper } from "hooks";
import { grey } from "@mui/material/colors";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function instanceOfCommuneFusionnee(object: any): object is CommuneAssocieeDeleguee {
  return (
    "type" in object &&
    (object.type === TypeCommuneAssocieeDeleguee.ASSOCIEE ||
      object.type === TypeCommuneAssocieeDeleguee.DELEGUEE)
  );
}

type CommuneLinkData1 = {
  commune: Commune | CommuneAssocieeDeleguee;
  id?: never;
  libelle?: never;
};

type CommuneLinkData2 = {
  commune?: never;
  id: string;
  libelle?: string;
};

type CommuneLinkCommonProps = {
  noLink?: boolean;
  displayComment?: boolean;
};

type CommuneLinkProps = LinkProps & CommuneLinkCommonProps & (CommuneLinkData1 | CommuneLinkData2);

function CommuneLink({
  commune,
  id,
  libelle,
  noLink = false,
  displayComment = false,
  ...linkProps
}: Readonly<CommuneLinkProps>): ReactNode {
  const { anchorEl, hoverTrigger, blurTrigger, hoverPopover, blurPopover, isPopoverOpen } =
    usePopper();

  const { getCollectiviteLibelleById } = useSearch();
  const [libelleCommune, setLibelleCommune] = useState("");
  const [link, setLink] = useState("");
  const { catchErrors, isLoading, setIsLoading } = useErrorHandler({
    dontThrow: true,
    default: () => {
      setLibelleAndLink("Commune inconnue", id ?? "");
    },
  });

  const setLibelleAndLink = useCallback(
    (libelle: string, link: string): void => {
      setLibelleCommune(libelle);
      setLink(link);
      setIsLoading(false);
    },
    [setIsLoading]
  );

  useEffect(() => {
    async function getCommune(): Promise<void> {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const c = await communesService.getById(id!);
      setLibelleAndLink(c.libelle, routesConfig.commune.getParameterPath(c.id));
    }

    if (commune != null) {
      setLibelleAndLink(
        commune.libelle,
        instanceOfCommuneFusionnee(commune)
          ? routesConfig.communeAssocieeDeleguee.getParameterPath(commune.id)
          : routesConfig.commune.getParameterPath(commune.id)
      );
    } else if (libelle != null) {
      setLibelleAndLink(libelle, routesConfig.commune.getParameterPath(id));
    } else {
      const collectiviteLibelle =
        getCollectiviteLibelleById(id, "COMMUNE") ??
        getCollectiviteLibelleById(id, "COMMUNEASSDEG");
      if (collectiviteLibelle != null && collectiviteLibelle !== "") {
        setLibelleAndLink(collectiviteLibelle, routesConfig.commune.getParameterPath(id));
      } else {
        void catchErrors(getCommune);
      }
    }
  }, [catchErrors, commune, getCollectiviteLibelleById, id, libelle, setLibelleAndLink]);

  if (isLoading) {
    return <TypographySkeleton sx={{ maxWidth: 175 }} />;
  }

  if (noLink) {
    return (
      <Typography component="span" color="primary" sx={{ fontWeight: 500 }}>
        {libelleCommune}
      </Typography>
    );
  } else if (
    !displayComment ||
    instanceOfCommuneFusionnee(commune) ||
    commune?.commentaire == null
  ) {
    return (
      <Link href={link} {...linkProps}>
        {libelleCommune}
      </Link>
    );
  } else {
    return (
      <>
        <Stack
          direction="row"
          sx={{ cursor: "pointer" }}
          alignItems="center"
          spacing={0.5}
          onMouseEnter={hoverTrigger}
          onMouseLeave={blurTrigger}>
          <Link href={link} {...linkProps}>
            {libelleCommune}
          </Link>
          <SmsIcon fontSize="inherit" sx={{ color: grey[500] }} />
        </Stack>
        <Popper
          open={isPopoverOpen}
          anchorEl={anchorEl}
          sx={{ zIndex: 1200 }}
          placement="top"
          keepMounted
          modifiers={popperModifiers}
          onMouseEnter={hoverPopover}
          onMouseLeave={blurPopover}
          transition>
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={{ enter: 300, exit: 100 }} easing="ease-out">
              <Card variant="outlined" sx={{ boxShadow: 1, maxWidth: "500px" }}>
                <CardContent sx={{ pb: 0 }}>
                  <Typography variant="subtitle2" color="info">
                    Commentaire sur {commune.libelle}
                  </Typography>
                </CardContent>
                <CardContent>
                  <Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {commune.commentaire?.valeur}
                  </Typography>
                </CardContent>
              </Card>
            </Fade>
          )}
        </Popper>
      </>
    );
  }
}

export default CommuneLink;
